import { useMemo, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2, Users, Package, Upload, X, AlertTriangle, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import OrgQuotasDialog from './OrgQuotasDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SUPPORTED_CURRENCIES } from '@/lib/formatCurrency';
import { ENTERPRISE_PLAN_ID } from '@/hooks/useOrgPlan';
import { getContractStatus, formatContractDate } from '@/lib/orgContract';

const NEAR_LIMIT_PCT = 80;

type ConsumptionLevel = 'ok' | 'warning' | 'critical';

function licenseConsumptionLevel(count: number, maxUsers: number | null | undefined): ConsumptionLevel {
  // null/undefined/0 = sin contrato. Si hay usuarios consumiendo, marcar crítico.
  if (!maxUsers || maxUsers <= 0) {
    return count > 0 ? 'critical' : 'ok';
  }
  const pct = (count / maxUsers) * 100;
  if (pct >= 100) return 'critical';
  if (pct >= NEAR_LIMIT_PCT) return 'warning';
  return 'ok';
}

const COUNTRIES = [
  "Afganistán","Albania","Alemania","Andorra","Angola","Antigua y Barbuda","Arabia Saudita","Argelia","Argentina","Armenia",
  "Australia","Austria","Azerbaiyán","Bahamas","Bangladés","Barbados","Baréin","Bélgica","Belice","Benín",
  "Bielorrusia","Birmania","Bolivia","Bosnia y Herzegovina","Botsuana","Brasil","Brunéi","Bulgaria","Burkina Faso","Burundi",
  "Bután","Cabo Verde","Camboya","Camerún","Canadá","Catar","Chad","Chile","China","Chipre",
  "Colombia","Comoras","Corea del Norte","Corea del Sur","Costa de Marfil","Costa Rica","Croacia","Cuba","Dinamarca","Dominica",
  "Ecuador","Egipto","El Salvador","Emiratos Árabes Unidos","Eritrea","Eslovaquia","Eslovenia","España","Estados Unidos","Estonia",
  "Etiopía","Filipinas","Finlandia","Fiyi","Francia","Gabón","Gambia","Georgia","Ghana","Granada",
  "Grecia","Guatemala","Guinea","Guinea Ecuatorial","Guinea-Bisáu","Guyana","Haití","Honduras","Hungría","India",
  "Indonesia","Irak","Irán","Irlanda","Islandia","Islas Marshall","Islas Salomón","Israel","Italia","Jamaica",
  "Japón","Jordania","Kazajistán","Kenia","Kirguistán","Kiribati","Kuwait","Laos","Lesoto","Letonia",
  "Líbano","Liberia","Libia","Liechtenstein","Lituania","Luxemburgo","Madagascar","Malasia","Malaui","Maldivas",
  "Malí","Malta","Marruecos","Mauricio","Mauritania","México","Micronesia","Moldavia","Mónaco","Mongolia",
  "Montenegro","Mozambique","Namibia","Nauru","Nepal","Nicaragua","Níger","Nigeria","Noruega","Nueva Zelanda",
  "Omán","Países Bajos","Pakistán","Palaos","Palestina","Panamá","Papúa Nueva Guinea","Paraguay","Perú","Polonia",
  "Portugal","Reino Unido","República Centroafricana","República Checa","República del Congo","República Democrática del Congo","República Dominicana","Ruanda","Rumania","Rusia",
  "Samoa","San Cristóbal y Nieves","San Marino","San Vicente y las Granadinas","Santa Lucía","Santo Tomé y Príncipe","Senegal","Serbia","Seychelles","Sierra Leona",
  "Singapur","Siria","Somalia","Sri Lanka","Suazilandia","Sudáfrica","Sudán","Sudán del Sur","Suecia","Suiza",
  "Surinam","Tailandia","Tanzania","Tayikistán","Timor Oriental","Togo","Tonga","Trinidad y Tobago","Túnez","Turkmenistán",
  "Turquía","Tuvalu","Ucrania","Uganda","Uruguay","Uzbekistán","Vanuatu","Vaticano","Venezuela","Vietnam",
  "Yemen","Yibuti","Zambia","Zimbabue"
];

const statusLabels: Record<string, string> = { active: 'Activa', inactive: 'Inactiva', suspended: 'Suspendida' };

interface OrgForm {
  name: string;
  logo_url: string;
  sector: string;
  status: string;
  country: string;
  currency: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  plan_tier: 'free' | 'enterprise';
  max_users: string;
  contract_start_date: string;
  contract_end_date: string;
  billing_type: 'paid' | 'courtesy';
  notes: string;
}

const emptyForm: OrgForm = { name: '', logo_url: '', sector: '', status: 'active', country: '', currency: 'COP', contact_name: '', contact_email: '', contact_phone: '', plan_tier: 'enterprise', max_users: '', contract_start_date: new Date().toISOString().slice(0, 10), contract_end_date: '', billing_type: 'paid', notes: '' };

export default function OrganizationsTab() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('*').order('name');
      if (error) throw error;
      return data;
    },
  });

  const { data: userCounts = {} } = useQuery({
    queryKey: ['org-license-counts'],
    queryFn: async () => {
      const PAGE_SIZE = 1000;
      const orgByUser: Record<string, string> = {};
      let page = 0;
      while (true) {
        const { data: pageData, error } = await supabase
          .from('profiles')
          .select('id, organization_id')
          .not('organization_id', 'is', null)
          .order('id') // ORDER BY estable: sin esto la paginación pierde/duplica filas
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        if (!pageData || pageData.length === 0) break;
        for (const p of pageData) {
          if (p.organization_id) orgByUser[p.id] = p.organization_id;
        }
        if (pageData.length < PAGE_SIZE) break;
        page++;
      }

      // Solo cuentan las licencias Enterprise activas efectivamente asignadas.
      const counts: Record<string, number> = {};
      const userIds = Object.keys(orgByUser);
      for (let i = 0; i < userIds.length; i += PAGE_SIZE) {
        const chunk = userIds.slice(i, i + PAGE_SIZE);
        const { data: subs, error } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .in('user_id', chunk)
          .eq('plan_id', ENTERPRISE_PLAN_ID)
          .eq('status', 'active');
        if (error) throw error;
        (subs ?? []).forEach((s) => {
          const orgId = orgByUser[s.user_id];
          if (orgId) counts[orgId] = (counts[orgId] || 0) + 1;
        });
      }
      return counts;
    },

  });


  const orgIdsSorted = (orgs as { id: string }[]).map((o) => o.id).sort().join(',');

  const { data: quotaHealthByOrg = {} as Record<string, ConsumptionLevel> } = useQuery({
    queryKey: ['org-quota-health', orgIdsSorted],
    enabled: (orgs as { id: string }[]).length > 0,
    queryFn: async () => {
      const orgIds = (orgs as { id: string }[]).map((o) => o.id);
      const worstByOrg: Record<string, ConsumptionLevel> = {};
      orgIds.forEach((id) => {
        worstByOrg[id] = 'ok';
      });

      const { data: quotas, error: qErr } = await supabase
        .from('organization_program_quotas')
        .select('organization_id, program_id, contracted_qty')
        .in('organization_id', orgIds);
      if (qErr) throw qErr;
      if (!quotas?.length) return worstByOrg;

      const { data: cohorts, error: cErr } = await supabase
        .from('cohorts')
        .select('id, organization_id, program_id')
        .in('organization_id', orgIds);
      if (cErr) throw cErr;

      const usedByOrgProgram: Record<string, number> = {};
      if (cohorts?.length) {
        const cohortIds = cohorts.map((c) => c.id);
        const { data: participants, error: pErr } = await supabase
          .from('cohort_participants')
          .select('cohort_id')
          .in('cohort_id', cohortIds);
        if (pErr) throw pErr;
        (participants || []).forEach((p) => {
          const cohort = cohorts.find((c) => c.id === p.cohort_id);
          if (!cohort?.program_id) return;
          const key = `${cohort.organization_id}:${cohort.program_id}`;
          usedByOrgProgram[key] = (usedByOrgProgram[key] || 0) + 1;
        });
      }

      for (const q of quotas) {
        const key = `${q.organization_id}:${q.program_id}`;
        const used = usedByOrgProgram[key] || 0;
        const contracted = Number(q.contracted_qty) || 0;
        const pct = contracted > 0 ? (used / contracted) * 100 : 0;
        let level: ConsumptionLevel = 'ok';
        if (contracted > 0 && used >= contracted) level = 'critical';
        else if (pct >= NEAR_LIMIT_PCT) level = 'warning';

        const oid = q.organization_id as string;
        const prev = worstByOrg[oid];
        if (level === 'critical') worstByOrg[oid] = 'critical';
        else if (level === 'warning' && prev !== 'critical') worstByOrg[oid] = 'warning';
      }
      return worstByOrg;
    },
  });

  const { data: sectors = [] } = useQuery({
    queryKey: ['industry_sectors'],
    queryFn: async () => {
      const { data, error } = await supabase.from('industry_sectors').select('*').order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const createMut = useMutation({
    mutationFn: async (input: any) => {
      const { error } = await supabase.from('organizations').insert(input);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['organizations'] }),
        qc.invalidateQueries({ queryKey: ['org-license-counts'] }),
        qc.invalidateQueries({ queryKey: ['org-quota-health'] }),
      ]);
    },
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, ...input }: any) => {
      const { error } = await supabase.from('organizations').update(input).eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['organizations'] }),
        qc.invalidateQueries({ queryKey: ['org-quota-health'] }),
      ]);
    },
  });
  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('organizations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['organizations'] }),
        qc.invalidateQueries({ queryKey: ['org-license-counts'] }),
        qc.invalidateQueries({ queryKey: ['org-quota-health'] }),
      ]);
    },
  });

  const [dialog, setDialog] = useState<{ open: boolean; item?: any }>({ open: false });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<OrgForm>(emptyForm);
  const [quotasOrg, setQuotasOrg] = useState<{ id: string; name: string } | null>(null);
  const [downgrade, setDowngrade] = useState<{ payload: any; userIds: string[] } | null>(null);
  const [downgradeBusy, setDowngradeBusy] = useState(false);


  const openDialog = (item?: any) => {
    if (item) {
      setForm({
        name: item.name || '',
        logo_url: item.logo_url || '',
        sector: item.sector || '',
        status: item.status || 'active',
        country: item.country || '',
        currency: item.currency || 'COP',
        contact_name: item.contact_name || '',
        contact_email: item.contact_email || '',
        contact_phone: item.contact_phone || '',
        plan_tier: item.plan_tier === 'free' ? 'free' : 'enterprise',
        max_users: item.max_users?.toString() || '',
        contract_start_date: item.contract_start_date || '',
        contract_end_date: item.contract_end_date || '',
        billing_type: item.billing_type === 'courtesy' ? 'courtesy' : 'paid',
        notes: item.notes || '',
      });
    } else {
      setForm(emptyForm);
    }
    setDialog({ open: true, item });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Solo se permiten imágenes', variant: 'destructive' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'La imagen no puede superar 2 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `org-logos/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage.from('content').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from('content').getPublicUrl(path);
      setForm(f => ({ ...f, logo_url: publicUrl }));
      toast({ title: 'Logo subido correctamente' });
    } catch {
      toast({ title: 'Error al subir el logo', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Licencias Enterprise activas de los usuarios de una organización */
  const fetchActiveEnterpriseLicenses = async (orgId: string) => {
    const { data: profs } = await supabase.from('profiles').select('id').eq('organization_id', orgId);
    const ids = (profs ?? []).map((p) => p.id);
    if (ids.length === 0) return [] as string[];
    const { data: subs } = await supabase
      .from('user_subscriptions')
      .select('user_id')
      .in('user_id', ids)
      .eq('plan_id', ENTERPRISE_PLAN_ID)
      .eq('status', 'active');
    return (subs ?? []).map((s) => s.user_id as string);
  };

  const persist = async (payload: any) => {
    if (dialog.item) {
      await updateMut.mutateAsync({ id: dialog.item.id, ...payload });
    } else {
      await createMut.mutateAsync(payload);
    }
    setDialog({ open: false });
    toast({ title: dialog.item ? 'Organización actualizada' : 'Organización agregada' });
  };

  const save = async () => {
    if (!form.name) return;
    if (form.plan_tier === 'enterprise') {
      if (!form.contract_start_date || !form.contract_end_date) {
        toast({ title: 'Define la fecha de inicio y de vencimiento del contrato', variant: 'destructive' });
        return;
      }
      if (form.contract_end_date <= form.contract_start_date) {
        toast({ title: 'La fecha de vencimiento debe ser posterior a la de inicio', variant: 'destructive' });
        return;
      }
    }
    const payload: any = {
      name: form.name,
      logo_url: form.logo_url || null,
      sector: form.sector || null,
      status: form.status as any,
      country: form.country || null,
      currency: form.currency || 'COP',
      contact_name: form.contact_name || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
      plan_tier: form.plan_tier,
      // En plan gratuito el cupo de licencias Enterprise siempre es 0
      max_users: form.plan_tier === 'free' ? 0 : (form.max_users ? parseInt(form.max_users) : null),
      contract_start_date: form.plan_tier === 'free' ? null : form.contract_start_date,
      contract_end_date: form.plan_tier === 'free' ? null : form.contract_end_date,
      billing_type: form.plan_tier === 'free' ? null : form.billing_type,
      notes: form.notes || null,
    };
    try {
      // Pasar a plan Gratuito revoca las licencias Enterprise activas: confirmar antes.
      if (dialog.item && form.plan_tier === 'free' && dialog.item.plan_tier !== 'free') {
        const licensed = await fetchActiveEnterpriseLicenses(dialog.item.id);
        if (licensed.length > 0) {
          setDowngrade({ payload, userIds: licensed });
          return;
        }
      }
      await persist(payload);
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  const confirmDowngrade = async () => {
    if (!downgrade) return;
    setDowngradeBusy(true);
    try {
      const { error } = await supabase
        .from('user_subscriptions')
        .update({ status: 'inactive' })
        .in('user_id', downgrade.userIds)
        .eq('plan_id', ENTERPRISE_PLAN_ID)
        .eq('status', 'active');
      if (error) throw error;
      await persist(downgrade.payload);
      qc.invalidateQueries({ queryKey: ['enterprise-license-stats'] });
      toast({ title: `Se revocaron ${downgrade.userIds.length} licencia(s) Enterprise` });
      setDowngrade(null);
    } catch {
      toast({ title: 'No se pudo revocar las licencias', variant: 'destructive' });
    } finally {
      setDowngradeBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); setDeleteId(null); toast({ title: 'Organización eliminada' }); }
    catch { toast({ title: 'Error al eliminar', variant: 'destructive' }); }
  };

  const set = (key: keyof OrgForm, value: string) => setForm(f => ({ ...f, [key]: value }));

  // Filters & search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sectorFilter, setSectorFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [licenseFilter, setLicenseFilter] = useState<string>('all'); // all | ok | warning | critical | none
  const [billingFilter, setBillingFilter] = useState<string>('all'); // all | paid | courtesy
  const billingCounts = useMemo(() => {
    const c = { paid: 0, courtesy: 0 };
    (orgs as any[]).forEach((o) => {
      if (o.plan_tier === 'free') return;
      if (o.billing_type === 'paid') c.paid++;
      else if (o.billing_type === 'courtesy') c.courtesy++;
    });
    return c;
  }, [orgs]);

  // Sort
  type SortColumn = 'name' | 'sector' | 'country' | 'contact_name' | 'licenses' | 'status' | 'created_at';
  type SortDir = 'asc' | 'desc';
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const toggleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDir('asc');
    }
  };
  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortColumn !== col) return <ChevronsUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const availableSectors = useMemo(() => {
    const s = new Set<string>();
    (orgs as any[]).forEach((o) => o.sector && s.add(o.sector));
    return Array.from(s).sort();
  }, [orgs]);

  const availableCountries = useMemo(() => {
    const s = new Set<string>();
    (orgs as any[]).forEach((o) => o.country && s.add(o.country));
    return Array.from(s).sort();
  }, [orgs]);

  const filteredOrgs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (orgs as any[]).filter((o) => {
      if (statusFilter !== 'all' && (o.status || '') !== statusFilter) return false;
      if (sectorFilter !== 'all' && (o.sector || '') !== sectorFilter) return false;
      if (countryFilter !== 'all' && (o.country || '') !== countryFilter) return false;
      if (billingFilter !== 'all' && (o.plan_tier === 'free' || o.billing_type !== billingFilter)) return false;
      if (licenseFilter !== 'all') {
        if (licenseFilter === 'none') {
          if (o.max_users && o.max_users > 0) return false;
        } else {
          const lvl = licenseConsumptionLevel(userCounts[o.id] || 0, o.max_users);
          if (lvl !== licenseFilter) return false;
        }
      }
      if (!q) return true;
      return (
        o.name?.toLowerCase().includes(q) ||
        o.sector?.toLowerCase().includes(q) ||
        o.country?.toLowerCase().includes(q) ||
        o.contact_name?.toLowerCase().includes(q) ||
        o.contact_email?.toLowerCase().includes(q)
      );
    });

    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      let av: any, bv: any;
      switch (sortColumn) {
        case 'licenses':
          av = userCounts[a.id] || 0;
          bv = userCounts[b.id] || 0;
          break;
        case 'created_at':
          av = a.created_at ? new Date(a.created_at).getTime() : 0;
          bv = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          av = (a[sortColumn] || '').toString().toLowerCase();
          bv = (b[sortColumn] || '').toString().toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [orgs, search, statusFilter, sectorFilter, countryFilter, licenseFilter, billingFilter, userCounts, sortColumn, sortDir]);

  const hasActiveFilters =
    search !== '' || statusFilter !== 'all' || sectorFilter !== 'all' || countryFilter !== 'all' || licenseFilter !== 'all' || billingFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSectorFilter('all');
    setCountryFilter('all');
    setLicenseFilter('all');
    setBillingFilter('all');
  };

  const { overLimitOrgs, nearLimitOrgs } = useMemo(() => {
    const over: { id: string; name: string; count: number; max: number }[] = [];
    const near: { id: string; name: string; count: number; max: number }[] = [];
    (orgs as any[]).forEach((o) => {
      const max = Number(o.max_users) || 0;
      if (max <= 0) return;
      const count = userCounts[o.id] || 0;
      if (count > max) over.push({ id: o.id, name: o.name, count, max });
      else if ((count / max) * 100 >= NEAR_LIMIT_PCT) near.push({ id: o.id, name: o.name, count, max });
    });
    over.sort((a, b) => (b.count - b.max) - (a.count - a.max));
    near.sort((a, b) => (b.count / b.max) - (a.count / a.max));
    return { overLimitOrgs: over, nearLimitOrgs: near };
  }, [orgs, userCounts]);


  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {filteredOrgs.length === orgs.length
            ? `${orgs.length} organizaciones registradas`
            : `${filteredOrgs.length} de ${orgs.length} organizaciones`}
          {` · ${billingCounts.paid} pagadas · ${billingCounts.courtesy} cortesía`}
        </p>
        <Button onClick={() => openDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
      </div>


      {/* License alerts */}
      {overLimitOrgs.length > 0 && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-destructive">
                {overLimitOrgs.length === 1
                  ? '1 organización superó sus licencias contratadas'
                  : `${overLimitOrgs.length} organizaciones superaron sus licencias contratadas`}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Aumenta el límite (<code>max_users</code>) en la organización o reduce los usuarios asignados.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {overLimitOrgs.slice(0, 8).map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setLicenseFilter('critical')}
                    className="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-background/60 px-2 py-1 text-xs hover:bg-destructive/10"
                  >
                    <span className="font-medium">{o.name}</span>
                    <span className="text-destructive font-semibold">{o.count}/{o.max}</span>
                    <span className="text-muted-foreground">(+{o.count - o.max})</span>
                  </button>
                ))}
                {overLimitOrgs.length > 8 && (
                  <button
                    type="button"
                    onClick={() => setLicenseFilter('critical')}
                    className="text-xs text-destructive underline px-1"
                  >
                    +{overLimitOrgs.length - 8} más
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {overLimitOrgs.length === 0 && nearLimitOrgs.length > 0 && (
        <div className="rounded-xl border border-warning/40 bg-warning/10 p-4 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-warning">
                {nearLimitOrgs.length === 1
                  ? '1 organización cerca del límite de licencias'
                  : `${nearLimitOrgs.length} organizaciones cerca del límite de licencias`}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Consumo ≥ {NEAR_LIMIT_PCT}% del cupo contratado. Considera ampliar antes de superarlo.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {nearLimitOrgs.slice(0, 8).map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setLicenseFilter('warning')}
                    className="inline-flex items-center gap-1 rounded-md border border-warning/30 bg-background/60 px-2 py-1 text-xs hover:bg-warning/10"
                  >
                    <span className="font-medium">{o.name}</span>
                    <span className="text-warning font-semibold">{o.count}/{o.max}</span>
                  </button>
                ))}
                {nearLimitOrgs.length > 8 && (
                  <button
                    type="button"
                    onClick={() => setLicenseFilter('warning')}
                    className="text-xs text-warning underline px-1"
                  >
                    +{nearLimitOrgs.length - 8} más
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters & search */}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, sector, país, contacto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full lg:w-40"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="active">Activa</SelectItem>
            <SelectItem value="inactive">Inactiva</SelectItem>
            <SelectItem value="suspended">Suspendida</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sectorFilter} onValueChange={setSectorFilter}>
          <SelectTrigger className="w-full lg:w-44"><SelectValue placeholder="Sector" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los sectores</SelectItem>
            {availableSectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="w-full lg:w-44"><SelectValue placeholder="País" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los países</SelectItem>
            <ScrollArea className="h-60">
              {availableCountries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </ScrollArea>
          </SelectContent>
        </Select>
        <Select value={licenseFilter} onValueChange={setLicenseFilter}>
          <SelectTrigger className="w-full lg:w-48"><SelectValue placeholder="Licencias" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las licencias</SelectItem>
            <SelectItem value="ok">Uso normal</SelectItem>
            <SelectItem value="warning">Cerca del límite</SelectItem>
            <SelectItem value="critical">En límite o superado</SelectItem>
            <SelectItem value="none">Sin contratar</SelectItem>
          </SelectContent>
        </Select>
        <Select value={billingFilter} onValueChange={setBillingFilter}>
          <SelectTrigger className="w-full lg:w-48"><SelectValue placeholder="Modalidad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las modalidades</SelectItem>
            <SelectItem value="paid">Pagadas ({billingCounts.paid})</SelectItem>
            <SelectItem value="courtesy">Cortesía ({billingCounts.courtesy})</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <X className="w-4 h-4" />Limpiar
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('name')}>
                  <span className="inline-flex items-center gap-1">Nombre <SortIcon col="name" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('sector')}>
                  <span className="inline-flex items-center gap-1">Sector <SortIcon col="sector" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('country')}>
                  <span className="inline-flex items-center gap-1">País <SortIcon col="country" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('contact_name')}>
                  <span className="inline-flex items-center gap-1">Contacto <SortIcon col="contact_name" /></span>
                </TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-center cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('licenses')}>
                  <span className="inline-flex items-center gap-1 justify-center">Licencias (usadas / contratadas) <SortIcon col="licenses" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('status')}>
                  <span className="inline-flex items-center gap-1">Estado <SortIcon col="status" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('created_at')}>
                  <span className="inline-flex items-center gap-1">Creada <SortIcon col="created_at" /></span>
                </TableHead>
                <TableHead className="w-24">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {orgs.length === 0 ? 'No hay organizaciones registradas.' : 'No hay organizaciones que coincidan con los filtros.'}
                  </TableCell>
                </TableRow>
              ) : filteredOrgs.map((o: any) => {
                const count = userCounts[o.id] || 0;
                return (
                  <TableRow key={o.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-7 h-7">
                          {o.logo_url ? <AvatarImage src={o.logo_url} alt={o.name} /> : null}
                          <AvatarFallback className="text-xs">{o.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{o.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{o.sector || '—'}</TableCell>
                    <TableCell>{o.country || '—'}</TableCell>
                    <TableCell>
                      {o.contact_name ? (
                        <div className="text-sm">
                          <span>{o.contact_name}</span>
                          {o.contact_email && <span className="block text-xs text-muted-foreground">{o.contact_email}</span>}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {o.plan_tier === 'free' ? (
                        <Badge variant="outline">Gratuito</Badge>
                      ) : (
                        <div className="flex flex-col items-start gap-1">
                          <div className="flex gap-1">
                            <Badge>Enterprise</Badge>
                            {o.billing_type === 'courtesy' && <Badge variant="secondary">Cortesía</Badge>}
                            {o.billing_type === 'paid' && <Badge variant="outline">Pagada</Badge>}
                          </div>
                          {o.contract_end_date && (() => {
                            const c = getContractStatus(o.contract_start_date, o.contract_end_date);
                            return c.isExpired ? (
                              <Badge variant="destructive" className="text-[10px]">Vencido {formatContractDate(o.contract_end_date)}</Badge>
                            ) : c.notStarted ? (
                              <Badge variant="outline" className="text-[10px]">Inicia {formatContractDate(o.contract_start_date)}</Badge>
                            ) : c.isExpiringSoon ? (
                              <Badge variant="outline" className="text-[10px] border-warning text-warning">Vence en {c.daysRemaining} días</Badge>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">Vigente hasta {formatContractDate(o.contract_end_date)}</span>
                            );
                          })()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const pct = o.max_users ? (count / o.max_users) * 100 : 0;
                        const licLevel = licenseConsumptionLevel(count, o.max_users);
                        const colorClass = o.max_users
                          ? licLevel === 'critical'
                            ? 'text-destructive font-semibold'
                            : licLevel === 'warning'
                              ? 'text-warning font-medium'
                              : 'text-foreground'
                          : 'text-muted-foreground';
                        const barColor =
                          licLevel === 'critical'
                            ? 'bg-destructive'
                            : licLevel === 'warning'
                              ? 'bg-warning'
                              : 'bg-success';
                        const licHint =
                          licLevel === 'critical'
                            ? 'Licencias Enterprise al límite o superadas'
                            : licLevel === 'warning'
                              ? `Consumo de licencias ≥ ${NEAR_LIMIT_PCT}% del contratado`
                              : 'Uso de licencias Enterprise';
                        return (
                          <div className="mx-auto flex max-w-[140px] flex-col items-stretch gap-1">
                            {o.max_users ? (
                              <div
                                className={cn(
                                  'h-1.5 w-full overflow-hidden rounded-full bg-muted',
                                  licLevel !== 'ok' && 'ring-1 ring-inset ring-current/25',
                                )}
                                title={licHint}
                              >
                                <div
                                  className={cn('h-full rounded-full transition-all', barColor)}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                            ) : null}
                            <div className={`flex items-center justify-center gap-1 ${colorClass}`}>
                              {licLevel !== 'ok' && o.max_users ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex shrink-0" aria-label={licHint}>
                                      <AlertTriangle className="h-3.5 w-3.5" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs">
                                    <p>{licHint}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                              <Users className="w-3.5 h-3.5 shrink-0" />
                              <span>{count}</span>
                              {o.max_users ? (
                                <span className="text-muted-foreground">/ {o.max_users}</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">sin límite</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell><Badge variant={o.status === 'active' ? 'default' : 'secondary'}>{statusLabels[o.status || ''] || o.status}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{format(new Date(o.created_at), 'dd MMM yyyy', { locale: es })}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={
                                quotaHealthByOrg[o.id] === 'critical'
                                  ? `Cupos por ruta: estado crítico para ${o.name}. Abrir gestión de cupos.`
                                  : quotaHealthByOrg[o.id] === 'warning'
                                    ? `Cupos por ruta: uso elevado para ${o.name}. Abrir gestión de cupos.`
                                    : `Cupos por ruta para ${o.name}: abrir gestión de cupos contratados y uso por ruta`
                              }
                              className={cn(
                                'relative',
                                quotaHealthByOrg[o.id] === 'critical' &&
                                  'text-destructive ring-2 ring-destructive/40 ring-offset-2 ring-offset-background',
                                quotaHealthByOrg[o.id] === 'warning' &&
                                  'text-warning ring-2 ring-warning/40 ring-offset-2 ring-offset-background',
                              )}
                              onClick={() => setQuotasOrg({ id: o.id, name: o.name })}
                            >
                              <Package className="w-4 h-4" />
                              {quotaHealthByOrg[o.id] === 'critical' ? (
                                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 rounded-full bg-destructive" aria-hidden />
                              ) : quotaHealthByOrg[o.id] === 'warning' ? (
                                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 rounded-full bg-warning" aria-hidden />
                              ) : null}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={6} className="max-w-[min(20rem,calc(100vw-1.5rem))] p-3 text-left">
                            {quotaHealthByOrg[o.id] === 'critical' ? (
                              <div className="space-y-1.5">
                                <p className="font-semibold text-destructive leading-snug">Cupos críticos</p>
                                <p className="text-sm text-popover-foreground/95 leading-relaxed">
                                  En al menos una ruta el uso alcanzó o superó el cupo contratado. Haz clic para revisar
                                  participantes por cohorte y ajustar cupos o contratación.
                                </p>
                              </div>
                            ) : quotaHealthByOrg[o.id] === 'warning' ? (
                              <div className="space-y-1.5">
                                <p className="font-semibold text-warning leading-snug">Cupos cerca del límite</p>
                                <p className="text-sm text-popover-foreground/95 leading-relaxed">
                                  En al menos una ruta el consumo supera el {NEAR_LIMIT_PCT}% del cupo contratado. Abre el
                                  panel para ver el detalle por ruta y planificar ampliación si hace falta.
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <p className="font-semibold leading-snug">Cupos por ruta</p>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  Define cuántos cupos tiene contratados esta organización por ruta y consulta cuántos
                                  participantes están asignados en las cohortes frente a cada cupo.
                                </p>
                                <p className="text-xs text-muted-foreground/90 pt-0.5 border-t border-border/60">
                                  Clic para abrir la gestión de cupos.
                                </p>
                              </div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                        <Button variant="ghost" size="icon" onClick={() => openDialog(o)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(o.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog.item ? 'Editar Organización' : 'Agregar Organización'}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Info general */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground">Información general</h4>
              <div><Label>Nombre *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nombre de la empresa" /></div>

              {/* Logo upload */}
              <div>
                <Label>Logo de la empresa</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Avatar className="w-14 h-14 border border-border">
                    {form.logo_url ? <AvatarImage src={form.logo_url} alt="Logo" /> : null}
                    <AvatarFallback className="text-lg">{form.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-1">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                      {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      {uploading ? 'Subiendo...' : 'Subir logo'}
                    </Button>
                    {form.logo_url && (
                      <Button type="button" variant="ghost" size="sm" className="text-destructive h-7 px-2" onClick={() => set('logo_url', '')}>
                        <X className="w-3 h-3 mr-1" />Quitar
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sector</Label>
                  <Select value={form.sector} onValueChange={v => set('sector', v)}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{sectors.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>País</Label>
                  <Select value={form.country} onValueChange={v => set('country', v)}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar país" /></SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-60">
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Moneda (ROI)</Label>
                  <Select value={form.currency || 'COP'} onValueChange={v => set('currency', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.status} onValueChange={v => set('status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de plan *</Label>
                  <Select value={form.plan_tier} onValueChange={v => set('plan_tier', v as OrgForm['plan_tier'])}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                      <SelectItem value="free">Gratuito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Licencias Enterprise contratadas *</Label>
                  <Input
                    type="number"
                    min={0}
                    required
                    disabled={form.plan_tier === 'free'}
                    value={form.plan_tier === 'free' ? '0' : form.max_users}
                    onChange={e => set('max_users', e.target.value)}
                    placeholder="Ej: 50 (0 = sin licencias)"
                  />
                  {form.plan_tier === 'free' && (
                    <p className="text-xs text-muted-foreground mt-1">En plan gratuito no hay licencias contratadas.</p>
                  )}
                </div>
              </div>
              {form.plan_tier === 'enterprise' && (
                <div>
                  <Label>Modalidad comercial *</Label>
                  <Select value={form.billing_type} onValueChange={v => set('billing_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Pagada</SelectItem>
                      <SelectItem value="courtesy">Cortesía</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Dato interno: no cambia el acceso de la empresa.</p>
                </div>
              )}
              {form.plan_tier === 'enterprise' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Fecha de inicio del contrato *</Label>
                    <Input type="date" value={form.contract_start_date} onChange={e => set('contract_start_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Fecha de vencimiento *</Label>
                    <Input type="date" value={form.contract_end_date} onChange={e => set('contract_end_date', e.target.value)} />
                    <p className="text-xs text-muted-foreground mt-1">Al vencer, todos los usuarios pasan a Gratuito sin perder su historial.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Contacto */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground">Contacto principal</h4>
              <div><Label>Nombre</Label><Input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Nombre del contacto" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="email@empresa.com" /></div>
                <div><Label>Teléfono</Label><Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} placeholder="+52 55 ..." /></div>
              </div>
            </div>

            {/* Notas */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground">Notas internas</h4>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Notas visibles solo para super admins..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false })}>Cancelar</Button>
            <Button onClick={save} disabled={!form.name}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta organización?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Asegúrate de que no tenga usuarios asociados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!downgrade} onOpenChange={(open) => !open && !downgradeBusy && setDowngrade(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Pasar esta empresa a plan Gratuito?</AlertDialogTitle>
            <AlertDialogDescription>
              {downgrade?.userIds.length} persona(s) de esta empresa tienen licencia Enterprise activa.
              Al continuar, el cupo queda en 0 y se les revocará la licencia: perderán el acceso a los
              módulos empresariales. Esta acción no se puede deshacer automáticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={downgradeBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDowngrade(); }}
              disabled={downgradeBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {downgradeBusy ? 'Revocando…' : 'Sí, revocar y pasar a Gratuito'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OrgQuotasDialog org={quotasOrg} open={!!quotasOrg} onOpenChange={(open) => !open && setQuotasOrg(null)} />
    </div>
  );
}